import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Divider } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../hooks/storeHooks";
import { ICorporateEvent, ICorporateEventBid } from "../../../../interfaces/corporateEvent";
import { getCorporateEventBidsThunk } from "../../../../store/b2b/corporateEvent/corporateEventThunk";
import QuoteCard from "../../quote-card/QuoteCard";
import IsMobile from "../../../../hooks/isMobile";
import { fDate } from "../../../../utils/formatTime";
import { isObjectEmpty } from "../../../../helpers/common";
import AdminMessageComponent from "../../admin-message/AdminMessageComponent";
import {
	CorporateEventBidStatusEnum,
	CorporateEventStatusEnum,
	CorporateEventVendorStatusEnum,
} from "../../../../constants/enums";
import QuotesEmptyState from "./quotes-empty-state/QuotesEmptyState";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../../../types/coreButtonTypes";
import CoreButtons from "../../../core-components/button/CoreButtons";
import PreviewModal from "../../../reusable-components/modals/preview-modal/PreviewModal";
import { iUserDetails } from "../../../../types/userType";
import { getPdfNameFromUrl } from "../../../../utils/commonUtils";
import InvoiceLink from "../../../reusable-components/invoice-link/InvoiceLink";
import { RoleEnum } from "../../../../types/eventTypes";
import { calculateQuoteAmount, getBrandedQuoteHtmlContent } from "../../../../utils/quoteFormUtils";
import { ROUTE_PATHS } from "../../../../routes";
import "./quotesTabContent.scss";

interface QuotesTabContentProps {
	isEventDeclined: boolean;
	corporateEventDetail: ICorporateEvent;
	onClickAddAdditionalQuote?: () => void;
}

const QuotesTabContent: React.FC<QuotesTabContentProps> = ({
	corporateEventDetail,
	onClickAddAdditionalQuote,
	isEventDeclined,
}) => {
	const isMobile = IsMobile();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { userDetails } = useAppSelector((state) => state.user);
	const {
		corporateEventBids: { currentBid, history },
		corporateEventBidsLoading,
		corporateEventBidsApiError,
	} = useAppSelector((state) => state.corporateEvent);
	const [showQuoteModal, setShowQuoteModal] = useState(false);
	const [quotePreviewHTML, setQuotePreviewHTML] = useState<string>("");

	const quotesCount = history?.length + (!isObjectEmpty(currentBid) ? 1 : 0);
	const isActiveEvent =
		corporateEventDetail?.status === CorporateEventStatusEnum.Active &&
		corporateEventDetail?.vendor?.status === CorporateEventVendorStatusEnum.AvailableForBidding;
	const invoices =
		corporateEventDetail?.invoices?.filter((invoice) => invoice.role === RoleEnum.Vendor) ?? [];
	const purchaseOrderPdfUrl = currentBid?.purchaseOrderPdfUrl ?? "";
	const adminMessage = corporateEventDetail?.vendor?.adminMessage ? corporateEventDetail?.vendor?.adminMessage : "";

	// Fetch bids when component mounts
	useEffect(() => {
		if (corporateEventDetail?.id) {
			fetchCorporateEventBids();
		}
	}, [dispatch, corporateEventDetail]);

	const fetchCorporateEventBids = () => {
		if (corporateEventDetail?.id) {
			dispatch(getCorporateEventBidsThunk(corporateEventDetail.id));
		}
	}

	const handleEditQuote = async (quote: ICorporateEventBid) => {
		navigate(`${ROUTE_PATHS.QUOTE_FORM}?corporateEventId=${corporateEventDetail?.id}`, { state: { quote } });
	};

	const handleViewQuote = async (quote: ICorporateEventBid) => {
		setQuotePreviewHTML(getBrandedQuoteHtmlContent(quote, userDetails as iUserDetails, true));
		setShowQuoteModal(true);
	};

	// Show loading state
	if (corporateEventBidsLoading) {
		return (
			<Box className="quotesTabContent">
				<Box className="quotesLoadingOrErrorContainer">
					<CircularProgress size={isMobile ? 16 : 24} />
					<Typography sx={{ mt: 2 }}>Loading quotes...</Typography>
				</Box>
			</Box>
		);
	}

	// Show error state
	if (corporateEventBidsApiError) {
		return (
			<Box className="quotesTabContent">
				<Box className="quotesLoadingOrErrorContainer">
					<Typography>Error loading quotes. Please try again.</Typography>
				</Box>
			</Box>
		);
	}

	//Show empty state
	const renderEmptyState = () => {
		return <QuotesEmptyState className="noQuotesMessage" onAddNewQuote={onClickAddAdditionalQuote} />;
	};

	return (
		<Box className={`quotesTabContent ${adminMessage && isEventDeclined ? "adminMessageQuoteTabContent" : ""}`}>
			<Box className="quotesList">
				<Box className="deadlineToQuoteContainer">
					<Typography className="sectionTitle">Deadline to quote</Typography>
					<Box className="deadlineToQuoteContainer_content">
						<Typography className="deadlineToQuoteContainer_content_title">Send quotes latest by:</Typography>
						<Typography className="deadlineToQuoteContainer_content_value">
							{fDate(corporateEventDetail?.externalDeadline, "MMM dd, yyyy")}
						</Typography>
					</Box>
				</Box>

				{isActiveEvent && <Divider className="quotesTabContent_divider" />}

				{quotesCount > 0 ? (
					<>
						{/* Latest Quote Section */}
						{!isObjectEmpty(currentBid) && (
							<>
								<Typography className="sectionTitle">
									{currentBid?.status === CorporateEventBidStatusEnum.Accepted
										? "Approved quote"
										: "Latest quote"}
								</Typography>
								<QuoteCard
									quoteAmount={calculateQuoteAmount(currentBid)}
									quoteDate={fDate(currentBid?.createdAt, "MMM dd, yyyy | hh:mm aaa")}
									notes={currentBid?.comment ?? ""}
									onEditQuote={() => handleEditQuote(currentBid)}
									onViewQuote={() => handleViewQuote(currentBid)}
									isLatest={true}
									isApproved={currentBid?.status === CorporateEventBidStatusEnum.Accepted}
									showEditButton={isActiveEvent}
									bidSubmittedByAdmin={currentBid?.bidSubmittedByAdmin}
								/>
							</>
						)}

						{(invoices?.length > 0 || purchaseOrderPdfUrl) &&
							currentBid?.status === CorporateEventBidStatusEnum.Accepted && (
								<Box className="invoicesList">
									{invoices?.map((invoice) => {
										return (
											<InvoiceLink
												key={invoice.id}
												invoiceName={`Download ${getPdfNameFromUrl(invoice.invoice)?.replace("self", "vendor")} pdf`}
												invoiceUrl={invoice.invoice}
											/>
										);
									})}
									{purchaseOrderPdfUrl && (
										<InvoiceLink
											key="purchaseOrderPdfUrl"
											invoiceName="Download purchase order pdf"
											invoiceUrl={purchaseOrderPdfUrl}
										/>
									)}
								</Box>
							)}

						{/* Quote History Section */}
						{history?.length > 0 && (
							<Box className="quotesHistoryList">
								<Typography className="sectionTitle">Quote history</Typography>
								{history?.map((quote) => {
									return (
										<QuoteCard
											key={quote.id}
											quoteAmount={calculateQuoteAmount(quote)}
											quoteDate={fDate(quote?.createdAt, "MMM dd, yyyy | hh:mm aaa")}
											onEditQuote={() => handleEditQuote(quote)}
											onViewQuote={() => handleViewQuote(quote)}
											isLatest={false}
											showEditButton={isActiveEvent}
											bidSubmittedByAdmin={quote?.bidSubmittedByAdmin}
										/>
									);
								})}
							</Box>
						)}

						{isActiveEvent && (
							<CoreButtons
								className="sendQuoteButton"
								fullWidth
								buttonText="Send new quote"
								variant={BUTTON_VARIANT.CONTAINED}
								color={BUTTON_COLOR.PRIMARY}
								onclick={onClickAddAdditionalQuote}
							/>
						)}
					</>
				) : isActiveEvent ? (
					renderEmptyState()
				) : null}
			</Box>

			{/* Admin Notes Section */}
			<AdminMessageComponent adminMessage={adminMessage} />

			<PreviewModal
				open={showQuoteModal}
				onClose={() => setShowQuoteModal(false)}
				htmlContent={quotePreviewHTML}
				title=""
			/>
		</Box>
	);
};

export default QuotesTabContent;
